import { api } from './api';
import { encryptBlob, encryptJson, toB64 } from '../../../core/crypto';
import { categoryFor, type AttachmentDragPayload } from './attachmentTypes';

/** Fired after an attachment upload succeeds. Consumed by AttachmentsList for live refresh. */
export const ATTACHMENTS_CHANGED_EVENT = 'dragonvault:attachments-changed';

export interface AttachmentsChangedDetail {
  customerId: string;
  attachmentId: string;
}

export async function uploadFile(
  file: File,
  customerId: string,
  vaultKey: CryptoKey,
): Promise<AttachmentDragPayload> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const blobEnc = await encryptBlob(buf, vaultKey);
  const mimetype = file.type || 'application/octet-stream';
  const filename = file.name || 'upload';
  const metaEnc = await encryptJson({ filename, mimetype }, vaultKey);
  const res = await api.uploadAttachment({
    customerId,
    metadataIv: metaEnc.iv,
    metadataCiphertext: metaEnc.ciphertext,
    blobIv: toB64(blobEnc.iv),
    blob: new Blob([blobEnc.ciphertext as BlobPart]),
  });
  window.dispatchEvent(
    new CustomEvent<AttachmentsChangedDetail>(ATTACHMENTS_CHANGED_EVENT, {
      detail: { customerId, attachmentId: res.id },
    }),
  );
  return {
    id: res.id,
    filename,
    mimetype,
    category: categoryFor(mimetype, filename),
  };
}

export async function uploadFiles(
  files: FileList | File[],
  customerId: string,
  vaultKey: CryptoKey,
): Promise<AttachmentDragPayload[]> {
  const arr = Array.from(files);
  const out: AttachmentDragPayload[] = [];
  for (const f of arr) out.push(await uploadFile(f, customerId, vaultKey));
  return out;
}

/** Returns `true` if the given drag event carries OS files (not just an in-app payload). */
export function dragHasFiles(e: React.DragEvent | DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes('Files');
}
